/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  AccountDataProperties,
  FacilitySidePanelInitialProperties,
  RequestFacilityAccountInterface,
} from '../../interface/facility-side-panel.interface';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import accountApi from '../../utils/api/account';

import { setNotificationMessage } from './NotificationManagementSlice';

// Async thunk to fetch facility account details
export const fetchFacilityAccountDetails = createAsyncThunk<
  AccountDataProperties & { limit: number; page: number; totalPages: number; totalResults: number },
  RequestFacilityAccountInterface
>(
  'facility-side-panel/fetchFacilityAccountDetails',
  async (data: RequestFacilityAccountInterface, { dispatch }) => {
    const { id, ...query } = data;
    try {
      const response = await accountApi.fetchFacilityAccount(id, removeEmptyKeys(query));
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Something went wrong!',
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

const initialState: FacilitySidePanelInitialProperties = {
  accountData: {
    chargingStationData: [],
  },
  loading: false,
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  sortBy: null,
  sortField: null,
};

export const facilitySidePanelSlice = createSlice({
  name: 'facility-side-panel',
  initialState,
  reducers: {
    /**
     * Set sort data
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setFacilitySortData: (
      state,
      action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>,
    ) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
    /**
     * Reset facility side panel
     * @param {any} state - The current state.
     */
    resetFacilitySidePanel: (state) => {
      state.accountData = initialState.accountData;
      state.limit = initialState.limit;
      state.page = initialState.page;
      state.totalPages = initialState.totalPages;
      state.totalResults = initialState.totalResults;
      state.sortBy = initialState.sortBy;
      state.sortField = initialState.sortField;
    },
    /**
     * Set facility limit
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setFacilityLimit: (state, action: PayloadAction<{ limit: number; page: number }>) => {
      state.limit = action.payload.limit;
      state.page = action.payload.page;
    },
  },
  /**
   * Extra reducers
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchFacilityAccountDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFacilityAccountDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.accountData = action.payload;
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchFacilityAccountDetails.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { setFacilitySortData, resetFacilitySidePanel, setFacilityLimit } =
  facilitySidePanelSlice.actions;

export default facilitySidePanelSlice.reducer;
